import type { Player } from './player.type';
import { actionHistory } from '../../store';
import type { Technique } from './techniques.type';
import type { Damage } from './damage.type';
import type { Die, DieCreation, DieRoller } from './die.type';
import { ModifierPriority, type ModifierParams, ModifierTags } from './modifier.type';
import type { StatusEffect } from './status-effects.type';
import type { Items } from './item.type';
import type { TraitType } from './trait-registry';

let count = 1;

export class Modifier {
  title: string;
  text: string;
  icon: string;
  bonusIcon?: string;
  stacks: number;
  id: number;
  /** Status effects, items and traits all share this class; each fills in its own kind of id. */
  type: StatusEffect | Items | TraitType;
  tags: Array<ModifierTags>;
  owner: Player;
  priority: ModifierPriority;
  duration: number;
  delay: boolean;
  properties: any;

  /*
    The roll hooks get the dice on the board and, in the same order, what
    rolls each one: `diceElements[i].roll()` rerolls `dice[i]`.
  */
  rollStart?(
    diceElements: Array<DieRoller | null | undefined>,
    dice: Array<Die>,
    roll: number,
  ): void;
  rollMid?(
    diceElements: Array<DieRoller | null | undefined>,
    dice: Array<Die>,
    promises: Array<Promise<void | boolean>>,
  ): void;
  rollEnd?(diceElements: Array<DieRoller | null | undefined>, dice: Array<Die>): void;
  getTechniques?(techniques: Array<Technique>): Array<Technique>;
  getDice?(dice: Array<DieCreation>): Array<DieCreation>;
  canUseTechnique?(technique: Technique, player: Player, enemy: Player): { able: boolean };
  canInterrupt?(technique: Technique, player: Player, enemy: Player): { able: boolean };
  useTechnique?(technique: Technique, target: Player, source: Player, die: Array<Die>): void;
  actionPhaseStart?(): void;
  beforeStartTurn?(): void;
  startTurn?({
    dice,
    rerollMax,
    activePlayer,
    inactivePlayer,
  }: {
    dice: Array<Die>;
    rerollMax: number;
    activePlayer: Player;
    inactivePlayer: Player;
  }): { dice: Array<Die>; rerollMax: number; activePlayer: Player; inactivePlayer: Player } | void;
  endTurn?(): void;
  checkDied?(): void;
  damageOut?(damage: Damage): void;
  damageIn?(damage: Damage): void;
  healingIn?(healing: Damage): void;
  modifierAdd?(type: StatusEffect, stacks: number, params?: ModifierParams): number;

  getHTML?(): string;
  onClick?(): void;

  constructor(mod?: Modifier) {
    this.id = count;
    count++;

    if (mod) {
      this.title = mod.title;
      this.text = mod.text;
      this.icon = mod.icon;
      this.stacks = mod.stacks;
      this.type = mod.type;
      this.tags = [...(mod.tags || [])];
      this.owner = mod.owner;
      this.priority = mod.priority || ModifierPriority.Middle;
      this.duration = mod.duration;
      this.delay = mod.delay;
      // copy properties object
      this.properties = { ...mod.properties };
    } else {
      this.title = 'Modifier';
      this.text = 'Modifier';
      this.icon = 'placeholder';
      this.stacks = 0;
      // A placeholder: every status effect, item and trait sets its own id.
      this.type = 'modifier' as Modifier['type'];
      this.tags = [];
      this.owner = {} as Player;
      this.priority = 0;
      this.duration = 0;
      this.delay = false;
      this.properties = {};
    }

    /* if (this.owner){
      this.init();
    } */
  }

  clone(newOwner: Player) {
    var clone = new (Object.getPrototypeOf(this).constructor)(this);
    clone.title = this.title;
    clone.text = this.text;
    clone.icon = this.icon;
    clone.stacks = this.stacks;
    clone.type = this.type;
    clone.priority = this.priority;
    clone.duration = this.duration;
    clone.delay = this.delay;
    clone.tags = [...this.tags];
    clone.properties = { ...this.properties };
    clone.owner = newOwner;

    return clone;
  }

  init(params?: ModifierParams) {
    if (actionHistory) {
      actionHistory.push(`Created ${this.stacks} ${this.type} on ${this.owner?.title}`);
    }
    this.update(0, params);
  }

  update(stacks: number, params?: ModifierParams) {
    if (stacks) {
      this.stacks += stacks;
      if (actionHistory) {
        actionHistory.push(`Added ${stacks} ${this.type} to ${this.owner?.title}.`);
      }
    }
  }

  halveStacks() {
    this.removeStacks(Math.ceil(this.stacks / 2));
  }

  removeStacks(stacks: number) {
    this.stacks -= stacks;
    if (actionHistory && stacks > 0) {
      actionHistory.push(`Removed ${stacks} ${this.type} from ${this.owner?.title}.`);
    }

    if (this.stacks <= 0) {
      this.remove();
    }
  }

  remove() {
    if (this.owner) {
      const index = this.owner.statusEffects.indexOf(this);
      if (index !== -1 && index >= 0) {
        this.owner?.statusEffects.splice(index, 1);
      }
    }
  }

  /*getOwner(){
    if(this.owner == 'Player'){
      return playerInstance;
    }
    else{
      return enemyInstance;
    }
  }*/
}
